import { Box, Card, CardContent, Divider, Grid, IconButton, Paper, Typography } from "@mui/material";
import DropdownComponent from "../../Components/DropdownComponent";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getGeneralMasterDropDownList } from "../../redux/Thunk/DashboardModule/DashboardThunk";
import TextFieldComponent from "../../Components/TextFieldComponent";
import { Add, Delete, FilterList } from "@mui/icons-material";
import Btn from "../../Components/Ui_elements/Btn";
import { filterCriteria, lqaResult, ratingOptions } from "../../Helper/Constants";
import DatePickerComponent from "../../Components/DatePickerComponent";
import DropdownMultiSelect from "../../Components/DropdownMultiSelect";
import { showSnackbar } from "../../redux/Slice/snackbar/snackbarSlice";
import { setAdvanceSearchProjectScoopData, setAdvanceSearchProjectScoopFilters } from "../../redux/Slice/ReportModule/ReportSlice";

const defaultFilter = {
    criteria: "",
    value: "",
    selectedOptions: [],
    startDate: "",
    endDate: "",
    sourceLang: "",
    targetLang: "",
    lqa_result: "",
    score: "",
};

const CRITERIA_MULTISELECT_DROPDOWN = [
    "direct_client",
    "indirect_client",
    "project_type",
    "scoop_status",
    "job_status",
    "pc",
    "pm",
    "qa",
    "linguist_in_job",
    "job_type",
];

const CRITERIA_WITHOUT_OPERATOR = [
    "project_update_type_follow_up",
    "project_update_type_qar",
];

const CRITERIA_DATE_INPUT = ["timeframe_scoop", "timeframe_job"];
const CRITERIA_TEXT_INPUT = [
    "scoop_name",
    "email_subject",
    "po_number",
    "project_number",
];

const CRITERIA_RATING_DROPDOWN = ["pm_score_rating", "qa_score_rating"];
const CRITERIA_WITH_RANGE = [
    "timeframe_scoop",
    "timeframe_job",
    "project_volume",
    "project_price",
    "job_price",
    "project_profit_margin",
    "scoop_profit_margin"
];

const AdvanceProjectSearch = ({ setPayloadData, setOpenModal }) => {
    const dispatch = useDispatch();
    const { jobs, dropdowns } = useSelector((state) => state.root.DashboardModule);
    const { advance_project_search_filters } = useSelector(state => state.root.ReportModule);
    const { clientList, indirectClientList, languages, projectType, scoopStatus, jobStatus, userCoordinator, userManager, userQaSpecialist, resource, } = dropdowns;
    const [errors, setErrors] = useState({});
    const [filters, setFilters] = useState([{ ...defaultFilter }]);

    const normalizeLegacyDmyToIso = (dateStr) => {
        if (!dateStr || typeof dateStr !== "string") return dateStr;

        // already ISO
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

        // supports d.m.Y / d-m-Y / d/m/Y (single-digit day/month allowed)
        const m = dateStr.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
        if (!m) return dateStr;

        const dd = String(m[1]).padStart(2, "0");
        const mm = String(m[2]).padStart(2, "0");
        const yyyy = m[3];
        return `${yyyy}-${mm}-${dd}`;
    };

    useEffect(() => {
        if (advance_project_search_filters?.filters && advance_project_search_filters?.filters.length > 0) {
            const persistedFilters = advance_project_search_filters.filters.map(f => {
                const newFilter = { ...f };
                // Reconstruct multi-select dropdown value if needed (logic below assumes 'value' handles simple string or split logic if complex)
                if (CRITERIA_MULTISELECT_DROPDOWN.includes(newFilter.criteria)) {
                    // Assuming values are comma separated strings, fetch corresponding objects if necessary
                    // For now, assume simple string values are handled or user resets filter
                    if (typeof newFilter.value === 'string') {
                        const options = getDynamicOptions(newFilter.criteria);
                        const selectedVals = newFilter.value.split(',');
                        newFilter.selectedOptions = options.filter(opt => selectedVals.includes(String(opt.value)));
                    }
                }

                // Backward-compat: older versions stored dates as d.m.Y in redux
                if (CRITERIA_DATE_INPUT.includes(newFilter.criteria)) {
                    newFilter.startDate = normalizeLegacyDmyToIso(newFilter.startDate);
                    newFilter.endDate = normalizeLegacyDmyToIso(newFilter.endDate);
                }
                return newFilter;
            });
            setFilters(persistedFilters);
        }
    }, [advance_project_search_filters, clientList, indirectClientList, languages, projectType, scoopStatus, jobStatus, userCoordinator, userManager, userQaSpecialist, resource]);

    useEffect(() => {
        dispatch(
            getGeneralMasterDropDownList({
                clientList: true,
                indirectClient: true,
                languages: true,
                projectType: true,
                scoopStatus: true,
                jobStatus: true,
                userCoordinator: 3,
                userManager: 2,
                userQaSpecialist: 4,
                resource: true,
                jobTypeList: true,
            })
        );
    }, []);

    const getDynamicOptions = (criteria) => {
        switch (criteria) {
            case "direct_client":
                return (
                    clientList?.map((client) => ({
                        label: client.vUserName,
                        value: client.iClientId,
                    })) || []
                );
            case "indirect_client":
                return (
                    indirectClientList?.map((account) => ({
                        label: account.vUserName,
                        value: account.iClientId,
                    })) || []
                );
            case "language_pair":
                return (
                    languages?.map((lang) => ({
                        label: lang.title,
                        value: lang.title,
                    })) || []
                );
            case "project_type":
                return (
                    projectType?.map((type) => ({
                        label: type.project_name,
                        value: type.pr_type_id,
                    })) || []
                );
            case "scoop_status":
                return (
                    scoopStatus?.map((scoop) => ({
                        label: scoop.item_status_name,
                        value: scoop.item_status_id,
                    })) || []
                );
            case "job_status":
                return (
                    jobStatus?.map((status) => ({
                        label: status.job_status_name,
                        value: status.job_status_name,
                    })) || []
                );
            case "pc":
                return (
                    userCoordinator?.map((coordinator) => ({
                        label: coordinator.vUserName,
                        value: coordinator.iUserId,
                    })) || []
                );
            case "pm":
                return (
                    userManager?.map((manager) => ({
                        label: manager.vUserName,
                        value: manager.iUserId,
                    })) || []
                );
            case "qa":
                return (
                    userQaSpecialist?.map((qa) => ({
                        label: qa.vUserName,
                        value: qa.iUserId,
                    })) || []
                );
            case "linguist_in_job":
                return (
                    resource?.map((linguist) => ({
                        label: linguist.vUserName,
                        value: linguist.iUserId,
                    })) || []
                );
            case "job_type":
                return (
                    jobs?.map((job) => ({
                        label: job.service_name,
                        value: job.job_id,
                    })) || []
                );
            default:
                return [];
        }
    };

    const isFilterComplete = (filter) => {
        if (!filter.criteria) return false;

        if (CRITERIA_MULTISELECT_DROPDOWN.includes(filter.criteria)) {
            return filter.selectedOptions && filter.selectedOptions.length > 0;
        }

        if (CRITERIA_DATE_INPUT.includes(filter.criteria)) {
            return filter.startDate && filter.endDate;
        }

        if (filter.criteria === "project_update_type_lqa") {
            return filter.lqa_result && filter.value;
        }

        if (filter.criteria === "language_pair") {
            return filter.sourceLang && filter.targetLang;
        }

        if (CRITERIA_WITH_RANGE.includes(filter.criteria)) {
            return filter.startDate && filter.endDate;
        }

        if (!CRITERIA_WITHOUT_OPERATOR.includes(filter.criteria)) {
            return !!filter.value;
        }

        return true;
    };
    const clearError = (fieldName) => {
        setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors[fieldName];
            return newErrors;
        });
    };

    const handleFilterChange = (index, field, value) => {
        const updatedFilters = [...filters];

        if (field === "criteria") {
            updatedFilters[index] = {
                ...defaultFilter,
                criteria: value,
            };

            // Clear all errors for this filter when criteria changes
            Object.keys(errors).forEach(key => {
                if (key.startsWith(`filter_${index}_`)) {
                    clearError(key);
                }
            });
        } else {
            updatedFilters[index] = {
                ...updatedFilters[index],
                [field]: value,
            };

            // Clear the specific error when the field is updated
            const errorKey = `filter_${index}_${field}`;
            if (errors[errorKey]) {
                clearError(errorKey);
            }
        }

        setFilters(updatedFilters);
    };

    const addFilter = () => {
        // Check if the last filter is complete
        if (filters.length > 0 && !isFilterComplete(filters[filters.length - 1])) {
            dispatch(
                showSnackbar({
                    message: "Please complete the current filter before adding a new one",
                    type: "error",
                })
            );
            return;
        }
        setFilters([...filters, { ...defaultFilter }]);
    };

    const removeFilter = (index) => {
        if (filters.length > 1) {
            const updatedFilters = filters.filter((_, i) => i !== index);
            setFilters(updatedFilters);

            // Clear any errors related to the removed filter
            Object.keys(errors).forEach(key => {
                if (key.startsWith(`filter_${index}_`)) {
                    clearError(key);
                }
            });
        }
    };

    const handleReset = () => {
        setFilters([defaultFilter]);
        setErrors({});
        dispatch(setAdvanceSearchProjectScoopData([]));
        dispatch(setAdvanceSearchProjectScoopFilters([]));
    }

    const renderFilterValue = (filter, index) => {
        const { criteria } = filter;

        if (!criteria) return null;

        // Multi-select dropdowns
        if (CRITERIA_MULTISELECT_DROPDOWN.includes(criteria)) {
            return (
                <DropdownMultiSelect
                    label="Value"
                    name="value"
                    selectedValue={filter.selectedOptions || []}
                    onSelect={(selectedOptions) => {
                        handleFilterChange(index, "selectedOptions", selectedOptions);
                    }}
                    options={getDynamicOptions(criteria)}
                    error={errors[`filter_${index}_value`] || ""}
                />
            );
        }

        // Rating dropdowns
        if (CRITERIA_RATING_DROPDOWN.includes(criteria)) {
            return (
                <DropdownComponent
                    label="Value"
                    name="value"
                    value={filter.value}
                    onChange={(e) => handleFilterChange(index, "value", e.target.value)}
                    options={ratingOptions || []}
                    error={errors[`filter_${index}_value`] || ""}
                />
            );
        }

        // Date inputs
        if (CRITERIA_DATE_INPUT.includes(criteria)) {
            return (
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <DatePickerComponent
                            id={`filter_${index}_startValue`}
                            name="startDate"
                            label="From Date"
                            value={filter.startDate}
                            onChange={(e) =>
                                handleFilterChange(index, "startDate", e.target.value)
                            }
                            fullWidth
                            error={errors[`filter_${index}_startValue`] || ""}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DatePickerComponent
                            id={`filter_${index}_endValue`}
                            name="endDate"
                            label="To Date"
                            value={filter.endDate}
                            onChange={(e) => {
                                const toDate = e.target.value;
                                const fromDate = filter.startDate;

                                if (fromDate && toDate && fromDate > toDate) {
                                    setErrors((prev) => ({
                                        ...prev,
                                        [`filter_${index}_endValue`]:
                                            "To date must be greater than or equal to from date",
                                    }));
                                } else {
                                    setErrors((prev) => ({
                                        ...prev,
                                        [`filter_${index}_endValue`]: "",
                                    }));
                                }

                                handleFilterChange(index, "endDate", toDate);
                            }}
                            fullWidth
                            error={errors[`filter_${index}_endValue`] || ""}
                        />
                    </Grid>
                </Grid>
            );
        }

        // LQA - two fields (Result and Score)
        if (criteria === "project_update_type_lqa") {
            return (
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <DropdownComponent
                            label="Result"
                            name="lqa_result"
                            value={filter.lqa_result}
                            onChange={(e) =>
                                handleFilterChange(index, "lqa_result", e.target.value)
                            }
                            options={lqaResult}
                            error={errors[`filter_${index}_lqa_result`] || ""}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextFieldComponent
                            id={`filter_${index}_value`}
                            name="value"
                            label="Score"
                            type="number"
                            value={filter.value}
                            onChange={(e) =>
                                handleFilterChange(index, "value", e.target.value)
                            }
                            fullWidth
                            onKeyDown={handleKeyDown}
                            error={errors[`filter_${index}_value`] || ""}
                        />
                    </Grid>
                </Grid>
            );
        }

        // Text inputs
        if (CRITERIA_TEXT_INPUT.includes(criteria)) {
            return (
                <TextFieldComponent
                    id={`filter_${index}_value`}
                    name="value"
                    label="Value"
                    type="text"
                    value={filter.value}
                    onChange={(e) => handleFilterChange(index, "value", e.target.value)}
                    fullWidth
                    onKeyDown={handleKeyDown}
                    error={errors[`filter_${index}_value`] || ""}
                />
            );
        }

        // Number inputs with range support
        if (CRITERIA_WITH_RANGE.includes(criteria)) {
            const label = ["project_profit_margin", "scoop_profit_margin"].includes(
                criteria
            )
                ? "Value (In %)"
                : "Value";

            return (
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextFieldComponent
                            id={`filter_${index}_startValue`}
                            name="startDate"
                            label={`From ${label}`}
                            type="number"
                            value={filter.startDate}
                            onChange={(e) =>
                                handleFilterChange(index, "startDate", e.target.value)
                            }
                            fullWidth
                            onKeyDown={handleKeyDown}
                            error={errors[`filter_${index}_startValue`] || ""}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextFieldComponent
                            id={`filter_${index}_endValue`}
                            name="endDate"
                            label={`To ${label}`}
                            type="number"
                            value={filter.endDate}
                            onChange={(e) => {
                                const toValue = parseFloat(e.target.value);
                                const fromValue = parseFloat(filter.startDate);

                                if (
                                    !isNaN(fromValue) &&
                                    !isNaN(toValue) &&
                                    fromValue > toValue
                                ) {
                                    setErrors((prev) => ({
                                        ...prev,
                                        [`filter_${index}_endValue`]:
                                            "To value must be greater than or equal to from value",
                                    }));
                                } else {
                                    setErrors((prev) => ({
                                        ...prev,
                                        [`filter_${index}_endValue`]: "",
                                    }));
                                }

                                handleFilterChange(index, "endDate", e.target.value);
                            }}
                            fullWidth
                            onKeyDown={handleKeyDown}
                            error={errors[`filter_${index}_endValue`] || ""}
                        />
                    </Grid>
                </Grid>
            );
        }

        // Language pair - single source and target dropdowns
        if (criteria === "language_pair") {
            return (
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <DropdownComponent
                            label="Source Language"
                            name="sourceLang"
                            value={filter.sourceLang}
                            onChange={(e) => handleFilterChange(index, "sourceLang", e.target.value)}
                            options={getDynamicOptions("language_pair")}
                            error={errors[`filter_${index}_sourceLang`] || ""}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DropdownComponent
                            label="Target Language"
                            name="targetLang"
                            value={filter.targetLang}
                            onChange={(e) => handleFilterChange(index, "targetLang", e.target.value)}
                            options={getDynamicOptions("language_pair")}
                            error={errors[`filter_${index}_targetLang`] || ""}
                        />
                    </Grid>
                </Grid>
            );
        }

        // Follow up - disabled text field with default value
        if (criteria === "project_update_type_follow_up") {
            return (
                <TextFieldComponent
                    id={`filter_${index}_value`}
                    name="value"
                    label="Value"
                    type="text"
                    value="FOLLOW UP"
                    onChange={(e) => handleFilterChange(index, "value", e.target.value)}
                    fullWidth
                    disabled
                    error={errors[`filter_${index}_value`] || ""}
                />
            );
        }

        if (criteria === "project_update_type_qar") {
            return (
                <TextFieldComponent
                    id={`filter_${index}_value`}
                    name="value"
                    label="Value"
                    type="text"
                    value="QAR"
                    onChange={(e) => handleFilterChange(index, "value", e.target.value)}
                    fullWidth
                    disabled
                    error={errors[`filter_${index}_value`] || ""}
                />
            );
        }

        // Default text input
        return (
            <TextFieldComponent
                id={`filter_${index}_value`}
                name="value"
                label="Value"
                type="text"
                value={filter.value}
                onChange={(e) => handleFilterChange(index, "value", e.target.value)}
                fullWidth
                onKeyDown={handleKeyDown}
                error={errors[`filter_${index}_value`] || ""}
            />
        );
    };

    const validateFilters = () => {
        const newErrors = {};
        let isValid = true;

        filters.forEach((filter, index) => {
            // Validate criteria is selected
            if (!filter.criteria) {
                newErrors[`filter_${index}_criteria`] = "Criteria is required";
                isValid = false;
            }

            // Validate value based on criteria type
            if (CRITERIA_MULTISELECT_DROPDOWN.includes(filter.criteria)) {
                if (!filter.selectedOptions || filter.selectedOptions.length === 0) {
                    newErrors[`filter_${index}_value`] = "At least one option must be selected";
                    isValid = false;
                }
            } else if (CRITERIA_DATE_INPUT.includes(filter.criteria)) {
                if (!filter.startDate || !filter.endDate) {
                    newErrors[`filter_${index}_startValue`] = !filter.startDate ? "From date is required" : "";
                    newErrors[`filter_${index}_endValue`] = !filter.endDate ? "To date is required" : "";
                    isValid = false;
                }
            } else if (filter.criteria === "project_update_type_lqa") {
                if (!filter.lqa_result) {
                    newErrors[`filter_${index}_lqa_result`] = "LQA result is required";
                    isValid = false;
                }
                if (!filter.value) {
                    newErrors[`filter_${index}_value`] = "LQA score is required";
                    isValid = false;
                }
            } else if (filter.criteria === "language_pair") {
                if (!filter.sourceLang) {
                    newErrors[`filter_${index}_sourceLang`] = "Source language is required";
                    isValid = false;
                }
                if (!filter.targetLang) {
                    newErrors[`filter_${index}_targetLang`] = "Target language is required";
                    isValid = false;
                }
            } else if (CRITERIA_WITH_RANGE.includes(filter.criteria)) {
                if (!filter.startDate || !filter.endDate) {
                    newErrors[`filter_${index}_startValue`] = !filter.startDate ? "From value is required" : "";
                    newErrors[`filter_${index}_endValue`] = !filter.endDate ? "To value is required" : "";
                    isValid = false;
                }
            } else if (!CRITERIA_WITHOUT_OPERATOR.includes(filter.criteria) && !filter.value) {
                newErrors[`filter_${index}_value`] = "Value is required";
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSave = async () => {
        if (!validateFilters()) {
            dispatch(
                showSnackbar({
                    message: "Please fix all validation errors before saving",
                    type: "error",
                })
            );
            return;
        }

        // Prepare the data to be saved
        const saveData = {
            filters: filters.map(filter => {
                const formattedFilter = { ...filter };

                // Format multi-select values
                if (filter.selectedOptions && filter.selectedOptions.length > 0) {
                    formattedFilter.value = filter.selectedOptions.map(opt => opt.value).join(",");
                    delete formattedFilter.selectedOptions;
                }

                // Keep date payload in ISO (yyyy-MM-dd). UI display formatting should stay in the picker.
                if (CRITERIA_DATE_INPUT.includes(filter.criteria)) {
                    formattedFilter.startDate = normalizeLegacyDmyToIso(formattedFilter.startDate);
                    formattedFilter.endDate = normalizeLegacyDmyToIso(formattedFilter.endDate);
                }

                return formattedFilter;
            }),
        };
        await setPayloadData(saveData);
        setOpenModal(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();   // ⭐ REQUIRED
            e.stopPropagation();  // prevents double firing
            handleSave();
        }
    };


    const getAvailableCriteriaOptions = (currentIndex) => {
        // Get all criteria that have been selected in other filters
        const selectedCriteria = filters
            .filter((_, index) => index !== currentIndex) // Exclude current filter
            .map(filter => filter.criteria)
            .filter(Boolean); // Remove empty/undefined criteria

        // Filter out the selected criteria from the full list
        return filterCriteria.filter(
            option => !selectedCriteria.includes(option.value)
        );
    };
    return (
        <Box sx={{ borderRadius: 2, overflow: 'hidden', }}>
            <CardContent sx={{ p: 3 }}>
                {filters.map((filter, index) => (
                    <Box key={index}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <DropdownComponent
                                    label="Criteria"
                                    name="criteria"
                                    value={filter.criteria}
                                    onChange={(e) => handleFilterChange(index, "criteria", e.target.value)}
                                    options={getAvailableCriteriaOptions(index)}
                                    error={errors[`filter_${index}_criteria`] || ""}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={11} md={7}>
                                {renderFilterValue(filter, index)}
                            </Grid>

                            <Grid item xs={1} sx={{ textAlign: 'right', mt: 3 }}>
                                {index > 0 && (
                                    <IconButton
                                        color="error"
                                        onClick={() => removeFilter(index)}
                                        size="small"
                                        sx={{ mb: 0.5 }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                ))}
                <>
                    <Box mt={2}>
                        <Btn
                            variant="outlined"
                            color="primary"
                            startIcon={<Add />}
                            onClick={addFilter}
                            size="small"
                        >
                            Add Filter
                        </Btn>
                    </Box>
                </>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* <Btn
                        variant="outlined"
                        color="primary"
                        startIcon={<Add />}
                        onClick={addFilter}
                        size="small"
                    >
                        Add Filter
                    </Btn> */}
                    <Btn
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        size="small"
                    >
                        Search
                    </Btn>
                    <Btn
                        variant="contained"
                        color="primary"
                        onClick={handleReset}
                        size="small"
                    >
                        Reset
                    </Btn>
                </Box>
            </CardContent>
        </Box>
    );
};

export default AdvanceProjectSearch;